import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../../../axiosInstance';
import { fetchSubFormsDropdownValues } from '../services/api';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Table from 'apollo-react/components/Table';
import CommonModal from '../../common/CommonModal';
import Typography from '../../common/StyledTypography';
import AutocompleteInput from '../../common/Form/autocompleteInput';
import TableActions from '../../common/DataTable/TableActions';

import { Formik, Form, Field } from 'formik';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import Button from '../../common/Button';
import Grid from 'apollo-react/components/Grid';
import SwitchInput from '../../common/Form/switchInput';
import TextInput from '../../common/Form/textInput';
import SelectInput from '../../common/Form/selectInput';
import useUnsavedChangesWarning from '../../../hooks/ui/navigation/useUnsavedChangesWarning';
import { createFormSchema } from '../../../validation/form';
import { useLicensingExperienceOptional } from '../../../contexts/LicensingExperienceContext';

const FormsCreate = ({
  persona,
  label,
  options,
  value,
  onChange,
  multiple,
}) => {
  const [formsTemplate, setFormsTemplate] = useState([]);
  const [subforms, setSubforms] = useState([]);
  const [subformsRows, setSubformsRows] = useState([]);
  const [draggedRowIndex, setDraggedRowIndex] = useState();
  const [hoveredRowIndex, setHoveredRowIndex] = useState();
  const [discardModal, setDiscardModal] = useState(false);
  const { mode } = useLicensingExperienceOptional();
  const isLicenseTracking = mode === 'license_tracking';
  const isLicensingTool = mode === 'licensing_tool';

  const [forms, setForms] = useState({
    name: '',
    description: '',
    published: false,
    subforms: [],
    template: false,
    has_hcp_licenses_list: false,
    attach_end_user_profile_snapshot: false,
  });
  const [t] = useTranslation();

  const isDirty = useMemo(() => {
    return (
      (forms.name || '').trim() !== '' ||
      (forms.description || '').trim() !== '' ||
      forms.published !== false ||
      forms.template !== false ||
      subformsRows.length > 0
    );
  }, [
    forms.name,
    forms.description,
    forms.published,
    forms.template,
    subformsRows,
  ]);

  const { navigateBack } = useUnsavedChangesWarning(isDirty);

  const handleFormChange = (name, value) => {
    if (name === 'form_id') {
      const selectedForm = formsTemplate.find(
        (form) => form.id === Number(value)
      );

      setForms({
        ...forms,
        [name]: value,
        subforms: selectedForm.subforms,
      });
      setSubformsRows(selectedForm.subforms);
    } else {
      setForms({
        ...forms,
        [name]: value,
      });
    }
  };

  const getFormsTemplate = async () => {
    try {
      const response = await axiosInstance.get(
        // eslint-disable-next-line no-undef
        `${process.env.REACT_APP_SERVER_URL}/api/forms-template`,
        { withCredentials: true }
      );
      setFormsTemplate(
        response.data.map((form) => ({
          name: form.name,
          description: form.description,
          published: form.published,
          id: form.id,
          template: 0,
          subforms: form.subforms,
        }))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const getSubforms = async () => {
    try {
      const { data } = await fetchSubFormsDropdownValues();
      setSubforms(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleFormSubmit = async () => {
    const updatedForms = { ...forms };
    updatedForms.subforms = subformsRows.map((subform, index) => ({
      ...subform,
      order: index,
    }));
    delete forms.id;

    const url = forms.template ? '/api/forms-template' : '/api/forms';
    try {
      await axiosInstance.post(url, updatedForms);
      toast.success(t('Form Saved Successfully!'), {
        position: 'top-center',
      });
      navigateBack();
    } catch (error) {
      console.error(error);
      const details = error.response?.data?.error?.details;
      if (details) {
        const messages = Array.isArray(details)
          ? details.map((d) => d.message || 'Validation error')
          : Object.values(details);
        toast.error(messages.join('\n'), { position: 'top-center' });
      } else {
        const message =
          error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message ||
          'Failed to save form.';
        toast.error(message, { position: 'top-center' });
      }
      return false;
    }
  };

  useEffect(() => {
    getFormsTemplate();
    getSubforms();
  }, []);

  const renderBasicFormInfo = () => (
    <div>
      <Field name="name">
        {({ field, meta }) => (
          <TextInput
            {...field}
            meta={meta}
            label={'Form name'}
            value={forms?.name || ''}
            onChange={(value) => handleFormChange('name', value)}
            required
          />
        )}
      </Field>

      <Field name="description">
        {({ field, meta }) => (
          <TextInput
            {...field}
            meta={meta}
            label={'Form Description'}
            value={forms?.description}
            onChange={(value) => handleFormChange('description', value)}
            multiline={true}
            textareaWidth={'100%'}
          />
        )}
      </Field>
    </div>
  );

  const ActionCell = ({ row }) => {
    return (
      <div style={{ display: 'flex', justifyContent: 'end' }}>
        <TableActions
          actions={[
            {
              text: 'Delete',
              color: 'secondary',
              style: {
                border: 'solid 1px #D9D9D9',
              },
              onClick: (row) => {
                const updatedSubForms = subformsRows.filter(
                  (subform) => subform.id !== row.id
                );
                setSubformsRows(updatedSubForms);
                setForms({
                  ...forms,
                  subforms: updatedSubForms,
                });
              },
            },
          ]}
          row={row}
        />
      </div>
    );
  };

  return (
    <div
      className="content"
      style={{
        border: 'solid 1px #E9E9E9',
        padding: '24px',
        borderRadius: '5px',
      }}
    >
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={12}>
          <Formik
            initialValues={forms}
            enableReinitialize={true}
            onSubmit={handleFormSubmit}
            validationSchema={toFormikValidationSchema(createFormSchema(t))}
          >
            <Form>
              {renderBasicFormInfo()}

              <Field name="form_id">
                {({ field, meta }) => (
                  <SelectInput
                    {...field}
                    meta={meta}
                    label={'Clone predefined Forms'}
                    options={
                      formsTemplate
                        ? formsTemplate.map((form) => ({
                            key: form.id,
                            value: form.id,
                            label: `${form.name}`,
                          }))
                        : []
                    }
                    onChange={(e, value) => {
                      handleFormChange('form_id', e);
                    }}
                    value={forms.form_id}
                  />
                )}
              </Field>

              <Field name="subforms">
                {({ field, meta }) => (
                  <AutocompleteInput
                    {...field}
                    meta={meta}
                    label={'Sub forms'}
                    options={subforms}
                    onChange={(e, value) => {
                      handleFormChange('subforms', e);
                      setSubformsRows(e);
                    }}
                    // required={true}
                    multiple={true}
                    value={forms?.subforms}
                  />
                )}
              </Field>
              <Typography
                variant="bodySmall"
                emphasis="low"
                gutterBottom
                style={{
                  marginTop: '35px',
                  textDecoration: 'underline',
                }}
              >
                {t('subforms_arrange')}
              </Typography>
              <Table
                columns={[
                  {
                    header: 'Sub forms',
                    accessor: 'label',
                  },
                  {
                    accessor: 'action',
                    customCell: ActionCell,
                    align: 'right',
                  },
                ]}
                rows={subformsRows}
                hidePagination
                rowId="id"
                rowProps={{
                  draggable: true,
                  style: { cursor: 'grab' },
                  onDragStart: (e) => {
                    const row = e.target.closest('tr');
                    const children = Array.from(
                      e.target.closest('tbody').children
                    );
                    setDraggedRowIndex(children.indexOf(row));
                    // console.log('draggedRowIndex', children.indexOf(row));
                  },
                  onDragOver: (e) => {
                    e.preventDefault();
                    const children = Array.from(
                      e.target.closest('tbody').children
                    );
                    const hoveredRow = e.target.closest('tr');
                    setHoveredRowIndex(children.indexOf(hoveredRow));
                  },
                  onDrop: () => {
                    // console.log('hoveredRowIndex', hoveredRowIndex);
                    const rowsCopy = [...subformsRows];
                    const movedRow = subformsRows[draggedRowIndex];
                    rowsCopy.splice(draggedRowIndex, 1);
                    rowsCopy.splice(hoveredRowIndex, 0, movedRow);
                    setSubformsRows(rowsCopy);
                    setForms({ ...forms, subforms: rowsCopy });
                    setHoveredRowIndex(null);
                    setDraggedRowIndex(null);
                  },
                }}
              />

              <Field name="template">
                {({ field, meta }) => (
                  <SwitchInput
                    {...field}
                    meta={meta}
                    label={t('Save as Template')}
                    checked={forms.template === 1}
                    value={forms?.template}
                    onChange={(checked) => {
                      handleFormChange('template', checked);
                    }}
                    style={{
                      marginTop: '16px',
                      marginBottom: '8px',
                    }}
                  />
                )}
              </Field>

              <Field name="published">
                {({ field, meta }) => (
                  <SwitchInput
                    {...field}
                    meta={meta}
                    label={t('Publish')}
                    checked={forms.published === 1}
                    value={forms?.published}
                    onChange={(checked) => {
                      handleFormChange('published', checked);
                    }}
                    style={{ marginTop: '16px' }}
                  />
                )}
              </Field>

              {isLicenseTracking ? (
                <SwitchInput
                  label="License Information"
                  helperText="Adds the License Information section to this form when enabled."
                  checked={!!forms.has_hcp_licenses_list}
                  onChange={(checked) =>
                    handleFormChange('has_hcp_licenses_list', checked)
                  }
                  style={{ marginTop: '8px', marginBottom: '4px' }}
                />
              ) : null}
              {isLicensingTool ? (
                <SwitchInput
                  label="Attach External-user profile on new requests"
                  helperText="When enabled, opening a new request from this form snapshots the applicant’s saved HCP profile on the request (reviewer copy)."
                  checked={!!forms.attach_end_user_profile_snapshot}
                  onChange={(checked) =>
                    handleFormChange(
                      'attach_end_user_profile_snapshot',
                      checked
                    )
                  }
                  style={{ marginTop: '8px', marginBottom: '4px' }}
                />
              ) : null}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginTop: '50px',
                }}
              >
                <Button
                  color="primary"
                  onClick={() => setDiscardModal(true)}
                  style={{
                    padding: '12px 22px',
                    width: '157px',
                    height: '40px',
                    marginRight: '24px',
                  }}
                  text={t('Discard')}
                />

                <Button
                  color="primary"
                  type="submit"
                  style={{
                    padding: '12px 22px',
                    width: '157px',
                    height: '40px',
                  }}
                  text={t('Save')}
                />
              </div>
            </Form>
          </Formik>
        </Grid>
      </Grid>

      <CommonModal
        open={discardModal}
        message="Are you sure you want to discard your changes?"
        buttonProps={[
          {
            onClick: () => {
              setDiscardModal(false);
            },
          },
          {
            label: 'Confirm',
            onClick: () => {
              setForms({
                name: '',
                description: '',
                published: false,
                subforms: [],
                template: false,
              });
              setSubformsRows([]);
              setDiscardModal(false);
            },
          },
        ]}
        hideHeader
        id="hideHeader"
      />
    </div>
  );
};

export default FormsCreate;
